import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgPen, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import { getWebpFileError } from "../../utils/fileValidation";
import DeleteConfirmModal from "./DeleteConfirmModal";
import ImageWithLoader from "./ImageWithLoader";
import ListHeader from "./ListHeader";
import ListPagination from "./ListPagination";
import Spinnerr from "./Spinner";
import Toaster from "./Toaster";

const getSortPosition = (item) => {
  const parsed = typeof item.position === "number" ? item.position : Number.NaN;
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const getSortDate = (item) => {
  const date = new Date(item.updatedAt || item.createdAt || 0).getTime();
  return Number.isNaN(date) ? 0 : date;
};

// Same order the apps use: position ascending (unset last), then latest updated first.
const sortItems = (items) =>
  [...items].sort((a, b) => getSortPosition(a) - getSortPosition(b) || getSortDate(b) - getSortDate(a));


/**
 * Shared list page for image content managed per app spot (Sliders, Banners).
 *
 * Handles fetching, search, pagination, bulk Active/Inactive/Delete, the
 * status badge toggle, view/delete and one Add/Edit modal. The common form fields
 * (title, image, position, status) live here; each page supplies its own fields:
 *
 * - extraColumns: [{ header, width?, render(item) }] — shown after the title.
 * - getEmptyForm() / toForm(item): page-specific form fields.
 * - validate(form) -> { field: message }.
 * - appendFormData(formData, form): page-specific multipart fields.
 * - renderLeadingFields / renderTrailingFields({ form, setField, errors, loading }).
 * - getViewDetails(item) -> [{ label, value }] for the view modal.
 */
const ContentListPage = ({
  title,
  singular,
  plural,
  endpoint,
  imageField,
  hint,
  extraColumns = [],
  getEmptyForm,
  toForm,
  validate,
  appendFormData,
  renderLeadingFields,
  renderTrailingFields,
  getViewDetails,
}) => {
  const Singular = singular.charAt(0).toUpperCase() + singular.slice(1);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);

  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedIds, setSelectedIds] = useState([]);

  const [formMode, setFormMode] = useState(null); // "add" | "edit" | null
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});

  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkDeleteModel, setBulkDeleteModel] = useState(false);

  const notify = (message, type = "success") => setSnack({ notification: { message, type } });

  const getItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(endpoint, { method: "GET" });
      const data = response.data;
      if (data?.s === 200) {
        setItems(sortItems(Array.isArray(data.r) ? data.r : []));
      } else {
        setItems([]);
        setSnack({
          notification: { message: data?.m || `Error fetching ${plural}`, type: "error" },
        });
      }
    } catch (err) {
      console.error(`Failed to fetch ${plural}:`, err);
      setItems([]);
      setSnack({ notification: { message: `Error fetching ${plural}`, type: "error" } });
    } finally {
      setLoading(false);
    }
  }, [endpoint, plural]);

  useEffect(() => {
    getItems();
  }, [getItems]);

  const filteredItems = items.filter((item) => {
    const search = query.trim().toLowerCase();
    return !search || item.title?.toLowerCase().includes(search);
  });

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const startIndex = (pageNo - 1) * limit;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + limit);

  const visibleIds = paginatedItems.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const countFailures = (results) =>
    results.filter((r) => r.status !== "fulfilled" || r.value?.data?.s !== 200).length;

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      notify(`Select at least one ${singular}`, "error");
      return;
    }
    if (action === "delete") {
      setBulkDeleteModel(true);
      return;
    }

    const isActive = action === "activate";
    const ids = selectedIds.filter((id) => items.find((item) => item._id === id)?.isActive !== isActive);
    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) =>
          AuthInterceptor(`${endpoint}/${id}`, {
            method: "PUT",
            body: JSON.stringify({ isActive }),
          })
        )
      );
      const failed = countFailures(results);
      if (failed) notify(`Updated ${ids.length - failed} of ${ids.length} ${plural}`, "error");
      else notify("Bulk action completed");
      setSelectedIds([]);
      getItems();
    } finally {
      setLoading(false);
    }
  };

  const bulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) => AuthInterceptor(`${endpoint}/${id}`, { method: "DELETE" }))
      );
      const failed = countFailures(results);
      if (failed) notify(`Deleted ${ids.length - failed} of ${ids.length} ${plural}`, "error");
      else notify(`Selected ${plural} deleted successfully`);
      setSelectedIds([]);
      getItems();
    } finally {
      setLoading(false);
      setBulkDeleteModel(false);
    }
  };

  const handleDelete = async () => {
    const id = deleteId;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`${endpoint}/${id}`, { method: "DELETE" });
      const data = response.data;
      if (data?.s === 200) {
        notify(`${Singular} deleted successfully`);
        setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
      } else {
        notify(data?.m || `Error deleting ${singular}`, "error");
      }
    } catch (err) {
      console.error(`Error deleting ${singular}:`, err);
      notify(err.message || `Error deleting ${singular}`, "error");
    } finally {
      getItems();
      setLoading(false);
      setDeleteId(null);
    }
  };

  const toggleStatus = async (item) => {
    const nextStatus = !item.isActive;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`${endpoint}/${item._id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: nextStatus }),
      });
      const data = response.data;
      if (data?.s === 200) {
        setItems((prev) =>
          prev.map((row) => (row._id === item._id ? { ...row, isActive: nextStatus } : row))
        );
        notify(`${Singular} status updated`);
      } else {
        notify(data?.m || `Error updating ${singular} status`, "error");
      }
    } catch (err) {
      notify(err.message || `Error updating ${singular} status`, "error");
    } finally {
      setLoading(false);
    }
  };

  // ---- Add / Edit form ----
  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  const openAdd = () => {
    setForm({ title: "", position: "", isActive: true, ...getEmptyForm() });
    setImage(null);
    setImagePreview("");
    setErrors({});
    setEditId(null);
    setFormMode("add");
  };

  const openEdit = (item) => {
    setForm({
      title: item.title || "",
      position: item.position ?? "",
      isActive: item.isActive ?? true,
      ...toForm(item),
    });
    setImage(null);
    setImagePreview(item.imageUrl || "");
    setErrors({});
    setEditId(item._id);
    setFormMode("edit");
  };

  const closeForm = () => {
    if (loading) return;
    setFormMode(null);
    setEditId(null);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEdit = formMode === "edit";
    const fileError = getWebpFileError(image);
    const nextErrors = Object.fromEntries(
      Object.entries({
        ...validate(form),
        title: String(form.title || "").trim() ? "" : `${Singular} title is required`,
        image: fileError || (!isEdit && !image ? "Image is required" : ""),
      }).filter(([, message]) => Boolean(message))
    );
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const formData = new FormData();
    formData.append("title", form.title.trim());
    formData.append("position", form.position === "" || form.position === null ? "" : String(form.position));
    formData.append("isActive", String(Boolean(form.isActive)));
    appendFormData(formData, form);
    if (image) formData.append(imageField, image);

    try {
      setLoading(true);
      const response = await AuthInterceptor(isEdit ? `${endpoint}/${editId}` : endpoint, {
        method: isEdit ? "PUT" : "POST",
        body: formData,
      });
      const data = response.data;
      if (data?.s === (isEdit ? 200 : 201)) {
        notify(`${Singular} ${isEdit ? "updated" : "added"} successfully`);
        setFormMode(null);
        setEditId(null);
        getItems();
      } else {
        notify(data?.m || `Error ${isEdit ? "updating" : "adding"} ${singular}`, "error");
      }
    } catch (err) {
      console.error(`Error saving ${singular}:`, err);
      notify(err.message || `Error saving ${singular}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = { form, setField, errors, loading };
  const colCount = 7 + extraColumns.length;

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}
      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title={title}
          searchValue={query}
          searchPlaceholder={`Search ${plural}...`}
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        />

        {hint && <div className="text-muted small mb-3">{hint}</div>}

        {/* Bulk actions | Add */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {paginatedItems.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
                Active
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
                Inactive
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
                Delete
              </button>
            </>
          )}
          <button className="btn btn-sm btn-outline-custom ms-auto" onClick={openAdd}>
            Add {Singular}
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%", minWidth: "900px" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label={`Select all ${plural} on this page`}
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>{Singular} Title</th>
                {extraColumns.map((column) => (
                  <th key={column.header} style={column.width ? { width: column.width } : undefined}>
                    {column.header}
                  </th>
                ))}
                <th style={{ width: "90px" }}>Position</th>
                <th style={{ width: "80px" }}>Image</th>
                <th style={{ width: "100px" }}>Status</th>
                <th style={{ width: "150px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedItems.map((item, idx) => (
                  <tr key={item._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(item._id)}
                        onChange={() => toggleRowSelection(item._id)}
                        aria-label={`Select ${singular}`}
                      />
                    </td>
                    <td>{startIndex + idx + 1}</td>
                    <td className="text-truncate" title={item.title}>
                      {item.title || "-"}
                    </td>
                    {extraColumns.map((column) => (
                      <td key={column.header}>{column.render(item)}</td>
                    ))}
                    <td>{item.position ?? "-"}</td>
                    <td>
                      {item.imageUrl ? (
                        <ImageWithLoader src={item.imageUrl} alt={item.title} style={{ width: "40px", height: "40px" }} />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${item.isActive ? "bg-success" : "bg-secondary"}`}
                        role="button"
                        onClick={() => toggleStatus(item)}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-row justify-content-start gap-2">
                        <button className="btn btn-sm btn-outline-custom" onClick={() => setViewItem(item)}>
                          <CgEye size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openEdit(item)}>
                          <CgPen size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(item._id)}>
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedItems.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="text-center text-muted py-4">
                    No {plural} found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={filteredItems.length > 0}
          pageNo={pageNo}
          totalPages={Math.ceil(filteredItems.length / limit) || 1}
          onPageChange={setPageNo}
        />
      </div>

      {/* Add / Edit Modal */}
      <Modal show={Boolean(formMode)} onHide={closeForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {formMode === "edit" ? "Edit" : "Add"} {Singular}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit} noValidate>
            {renderLeadingFields?.(fieldProps)}
            <div className="mb-3">
              <label className="form-label">{Singular} Title</label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                value={form.title || ""}
                onChange={(e) => setField("title", e.target.value)}
                disabled={loading}
              />
              {errors.title && <div className="invalid-feedback">{errors.title}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">Image</label>
              <input
                type="file"
                className={`form-control ${errors.image ? "is-invalid" : ""}`}
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setImage(fileError ? null : file || null);
                  setErrors((prev) => ({ ...prev, image: fileError }));
                }}
                disabled={loading}
              />
              <div className="form-text">WebP, PNG, JPG or JPEG, max 2MB.</div>
              {errors.image && <div className="invalid-feedback d-block">{errors.image}</div>}
              {imagePreview && !image && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Current Image</div>
                  <ImageWithLoader
                    src={imagePreview}
                    alt={form.title}
                    style={{ width: "96px", height: "54px", objectFit: "cover" }}
                  />
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={form.position ?? ""}
                onChange={(e) => setField("position", e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={form.isActive ? "true" : "false"}
                onChange={(e) => setField("isActive", e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            {renderTrailingFields?.(fieldProps)}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {formMode === "edit" ? "Update" : "Add"} {Singular}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      {/* View Modal */}
      <Modal show={Boolean(viewItem)} onHide={() => setViewItem(null)} centered size="md">
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="fw-semibold fs-6">{Singular} Information</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          {viewItem && (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-start">
                {viewItem.imageUrl ? (
                  <div className="border rounded-3 overflow-hidden" style={{ maxWidth: "460px", maxHeight: "220px" }}>
                    <ImageWithLoader src={viewItem.imageUrl} alt={singular} className="w-100" style={{ objectFit: "cover" }} />
                  </div>
                ) : (
                  <span className="text-muted small">No image available</span>
                )}
              </div>
              <div className="border rounded-3 p-3 d-flex flex-column gap-2">
                {[
                  { label: "Title", value: viewItem.title || "-" },
                  ...getViewDetails(viewItem),
                  { label: "Position", value: viewItem.position ?? "-" },
                  { label: "Status", value: viewItem.isActive ? "Active" : "Inactive" },
                ].map((detail) => (
                  <div key={detail.label}>
                    <div className="text-muted small">{detail.label}</div>
                    <div className="fw-medium text-break">{detail.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={Boolean(deleteId)}
        count={1}
        singular={singular}
        plural={plural}
        loading={loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
      <DeleteConfirmModal
        show={bulkDeleteModel}
        count={selectedIds.length}
        singular={singular}
        plural={plural}
        loading={loading}
        onConfirm={bulkDelete}
        onCancel={() => setBulkDeleteModel(false)}
      />
    </>
  );
};

export default ContentListPage;
